import React, {
  FC, useEffect, useMemo, useRef, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { Spin } from 'antd';
import moment from 'moment-timezone';
import type { BookingValues, FeeValues } from '../types/flow';
import NavRow from '../../assessment-portal/components/navigation-row';
import BottomSheet from '../../assessment-portal/components/bottom-sheet';
import FeeSummaryDetails from '../components/fee-summary-details';
import { useGetSpecialistsQuery } from '../../services/specialists';
import { AppointmentType } from '../../model';
import useTimeslotSelection from '../../assessment-portal/hooks/use-timeslot-selection';
import useCalendar from '../../assessment-portal/hooks/use-calendar';
import useSpecialistAvailabilities, {
  getEarliestSlot, dayHasFutureSlot,
} from '../hooks/use-specialist-availabilities';
import type { SpecialistAvailability } from '../hooks/use-specialist-availabilities';
import useAvailabilityPolling from '../hooks/use-availability-polling';
import useTimeSegments from '../hooks/use-time-segments';
import { parseAppointmentLengthMinutes } from '../../utils/expand-timeslots';
import { SubmitErrorAlert } from '../components/submit-error-alert';
import FeeUnavailableAlert from '../components/fee-unavailable-alert';
import FeeAmountLoading from '../components/fee-amount-loading';
import BookingIntro from '../components/booking/booking-intro';
import ClinicianOptions from '../components/booking/clinician-options';
import DesktopCalendar from '../components/booking/desktop-calendar';
import MobileDateStrip from '../components/booking/mobile-date-strip';
import TimeGrid from '../components/booking/time-grid';
import FeeFooterRow from '../components/booking/fee-footer-row';
import { DesktopStepHeading, MobileBookingSection } from '../components/booking/booking-sections';
import type { SubmissionFailure } from '../hooks/submission-state';
import { useSlotsLoadedTracking, useStepExitTracking } from '../hooks/use-booking-step-analytics';
import {
  browserTimezone,
  buildClinicianSelectedParams,
  deriveBookingStage,
  deriveFeeStatus,
  trackBackClick,
  trackClinicianSelected,
  trackConfirmClick,
  trackConfirmError,
  trackDateSelected,
  trackFeeDetailsOpen,
  trackFeeRetry,
  trackFeeUnavailable,
  trackMonthChanged,
  trackSelectionLost,
  trackSlotSelected,
  trackTimeSegmentSelected,
} from '../utils/booking-analytics';
import type { BookingTiming, MonthDirection, SoonestSlot } from '../utils/booking-analytics';
import type { TimeSegment } from '../utils/booking-slots';
import type Specialist from '../../model/Specialist';
import { formatTime, isSlotStartInFuture } from '../utils/booking-slots';
import type { FeeQuote } from '../utils/fee';

const specialistName = (specialist: Specialist): string => `${specialist.firstName} ${specialist.lastName}`;

const soonestSlotOf = (availability: SpecialistAvailability, appointmentLengthMinutes: number): SoonestSlot => (
  availability.isLoading ? 'loading' : getEarliestSlot(availability, appointmentLengthMinutes)
);

interface BookingPageProps {
  defaultValues: BookingValues;
  submitting: boolean;
  /** True from the moment the booking succeeds, before the page re-renders. */
  bookingConfirmedRef: { readonly current: boolean };
  /** Analytics timing for this visit, for the confirm events. */
  bookingTimingRef: { readonly current: BookingTiming };
  /** Records when the patient picks a time, for the confirm events. */
  onSlotSelected: () => void;
  failure?: SubmissionFailure;
  feeEstimate: FeeQuote | null;
  /** The fee quote is being fetched. */
  feeLoading: boolean;
  /** Medicare or DVA card holder; rebate wording is hidden without a card. */
  hasMedicareCard: boolean;
  /** The fee quote failed to load, so no fee can be shown or submitted. */
  feeUnavailable: boolean;
  onRetryFee: () => void;
  feePrescriptionType: FeeValues['prescriptionType'];
  feeLocation: FeeValues['location'];
  feeAssessmentType: FeeValues['feeAssessmentScope'];
  feeSeenPsychiatrist: FeeValues['seenPsychiatrist'];
  onBack: () => void;
  onSubmit: (values: BookingValues) => void;
}

const BookingPage: FC<BookingPageProps> = ({
  defaultValues,
  submitting,
  bookingConfirmedRef,
  bookingTimingRef,
  onSlotSelected,
  failure,
  feeEstimate,
  feeLoading,
  hasMedicareCard,
  feeUnavailable,
  onRetryFee,
  feePrescriptionType,
  feeLocation,
  feeAssessmentType,
  feeSeenPsychiatrist,
  onBack,
  onSubmit,
}) => {
  const methods = useForm<BookingValues>({
    defaultValues: defaultValues || {
      clinicianId: '',
      clinicianName: '',
      clinicianTitle: '',
      startDateTime: '',
      endDateTime: '',
    },
  });

  const { watch, setValue, handleSubmit } = methods;
  const values = watch();

  const { data: specialists, isLoading: isLoadingSpecialists } = useGetSpecialistsQuery(
    process.env.REACT_APP_ADHD_SPECIALTY_ID as string,
    { refetchOnMountOrArgChange: true },
  );

  const {
    getAvailability, loadMonth, lastRefreshCompletion, lastLoadFailure,
  } = useSpecialistAvailabilities(
    specialists,
    AppointmentType.NEW,
  );

  const calendar = useCalendar();

  const selectedAvailability = getAvailability(values.clinicianId);
  const availableTimeList = selectedAvailability.availableTimeByMonth[calendar.currentMonthString];
  const isLoadingAvailabilities = !!values.clinicianId && availableTimeList === undefined;

  const [openSection, setOpenSection] = useState<1 | 2 | 3 | 0>(1);
  const [sheetOpen, setSheetOpen] = useState(false);

  useAvailabilityPolling(values.clinicianId, calendar.currentMonthString, loadMonth);

  const selectedSpecialist = useMemo(
    () => specialists?.find((s) => s.id === values.clinicianId),
    [specialists, values.clinicianId],
  );

  const appointmentLength = useMemo(
    () => parseAppointmentLengthMinutes(selectedSpecialist?.appointmentLength),
    [selectedSpecialist],
  );

  const clinicianName = selectedSpecialist ? specialistName(selectedSpecialist) : '';

  const nextAvailable = useMemo(
    () => (values.clinicianId ? soonestSlotOf(selectedAvailability, appointmentLength) : null),
    [values.clinicianId, selectedAvailability, appointmentLength],
  );

  useSlotsLoadedTracking({
    clinicianId: values.clinicianId,
    clinicianName,
    month: calendar.currentMonthString,
    days: availableTimeList,
    appointmentLengthMinutes: appointmentLength,
    nextAvailable,
    lastLoadFailure,
  });

  const availableDates = useMemo(
    () => (availableTimeList
      ? availableTimeList.filter((day) => dayHasFutureSlot(day, appointmentLength)).map((at) => at.date).sort()
      : []),
    [availableTimeList, appointmentLength],
  );

  const currentMonthPrefix = `${calendar.calendarYear}-${String(calendar.calendarMonth + 1).padStart(2, '0')}`;
  const monthAvailableDates = useMemo(
    () => availableDates.filter((d) => d.startsWith(currentMonthPrefix)),
    [availableDates, currentMonthPrefix],
  );

  const timeslots = useTimeslotSelection(availableTimeList, calendar.selectedDate, String(appointmentLength));

  const {
    timeSegment, setTimeSegment, slotsBySegment, activeSegments,
  } = useTimeSegments(timeslots.availableTimeslots);

  const [localFailure, setLocalFailure] = useState<SubmissionFailure | null>(null);

  const hasBookableSelectedSlot = timeslots.availableTimeslots.some((slot) => (
    slot.start === timeslots.selectedSlotStart
    && isSlotStartInFuture(calendar.selectedDate, slot.start)
  ));

  useEffect(() => {
    if (!timeslots.selectedSlotStart || !availableTimeList) return;
    if (!calendar.selectedDate.startsWith(currentMonthPrefix)) return;
    if (hasBookableSelectedSlot) return;

    timeslots.resetSelection();
    setValue('startDateTime', '');
    setValue('endDateTime', '');
    setLocalFailure({ kind: 'slot-expired' });
    trackSelectionLost('slot_expired');
    setOpenSection(3);
  }, [availableTimeList, calendar.selectedDate, currentMonthPrefix, hasBookableSelectedSlot, timeslots, setValue]);

  // A completion is consumed once, before any early return, so later renders
  // cannot replay it against a date the user picked in the meantime.
  const consumedRefreshAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!lastRefreshCompletion) return;
    if (consumedRefreshAtRef.current === lastRefreshCompletion.completedAt) return;
    consumedRefreshAtRef.current = lastRefreshCompletion.completedAt;

    if (lastRefreshCompletion.specialistId !== values.clinicianId) return;
    if (lastRefreshCompletion.month !== calendar.currentMonthString) return;
    if (!calendar.selectedDate || timeslots.selectedSlotStart) return;
    if (!calendar.selectedDate.startsWith(currentMonthPrefix)) return;
    if (!availableTimeList) return;

    const selectedDay = availableTimeList.find((day) => day.date === calendar.selectedDate);
    if (selectedDay && dayHasFutureSlot(selectedDay, appointmentLength)) return;

    calendar.setSelectedDate('');
    timeslots.resetSelection();
    setValue('startDateTime', '');
    setValue('endDateTime', '');
    setLocalFailure({ kind: 'date-unavailable' });
    trackSelectionLost('date_unavailable');
    setOpenSection(2);
  }, [lastRefreshCompletion, values.clinicianId, calendar, currentMonthPrefix, availableTimeList, appointmentLength, timeslots, setValue]);

  const reportClinicianSelected = (specialistId: string) => {
    const chosen = specialists?.find((s) => s.id === specialistId);
    if (!specialists || !chosen) return;
    const earliestOf = (specialist: Specialist) => soonestSlotOf(
      getAvailability(specialist.id),
      parseAppointmentLengthMinutes(specialist.appointmentLength),
    );
    // Any clinician still loading could hold the soonest slot, so the
    // comparison is only reported once every lookahead has finished.
    const soonestAnyClinician = specialists
      .map(earliestOf)
      .reduce<SoonestSlot>((soonest, slot) => {
        if (soonest === 'loading' || slot === 'loading') return 'loading';
        return slot && (!soonest || slot.isBefore(soonest)) ? slot : soonest;
      }, null);
    trackClinicianSelected(buildClinicianSelectedParams(
      specialistName(chosen),
      earliestOf(chosen),
      soonestAnyClinician,
      browserTimezone(),
    ));
  };

  const handleSelectClinician = (specialistId: string) => {
    if (values.clinicianId === specialistId) return;
    reportClinicianSelected(specialistId);
    setValue('clinicianId', specialistId);
    calendar.setSelectedDate('');
    timeslots.resetSelection();
    setValue('startDateTime', '');
    setValue('endDateTime', '');
    setLocalFailure(null);
    setOpenSection(2);
  };

  const handleChangeMonth = (direction: MonthDirection) => {
    const step = direction === 'next' ? 1 : -1;
    trackMonthChanged(direction, moment(calendar.currentMonthString, 'YYYYMM').add(step, 'month').format('YYYYMM'));
    if (direction === 'next') {
      calendar.handleNextMonth();
    } else {
      calendar.handlePreviousMonth();
    }
  };

  const handleSelectDay = (day: number | null) => {
    const dayKey = calendar.getDayKey(day);
    if (calendar.isAvailableDay(day, availableDates) && dayKey !== calendar.selectedDate) {
      trackDateSelected(clinicianName, dayKey);
    }
    calendar.handleSelectDay(day, availableDates);
    timeslots.resetSelection();
    setValue('startDateTime', '');
    setValue('endDateTime', '');
    setLocalFailure(null);
    setOpenSection(3);
  };

  const handleSelectDateFromStrip = (dateKey: string) => {
    const day = parseInt(dateKey.split('-')[2], 10);
    handleSelectDay(day);
  };

  const handleSelectSlot = (slot: { start: string; end: string }) => {
    if (slot.start !== timeslots.selectedSlotStart) {
      trackSlotSelected(clinicianName, calendar.selectedDate, slot.start);
      onSlotSelected();
    }
    const { startDateTime, endDateTime } = timeslots.selectTimeslot(
      slot.start,
      slot.end,
      selectedSpecialist!.timezone,
    );
    setValue('startDateTime', startDateTime);
    setValue('endDateTime', endDateTime);
    setLocalFailure(null);
    setOpenSection(0);
  };

  const handleSelectSegment = (segment: TimeSegment) => {
    if (segment !== timeSegment) trackTimeSegmentSelected(segment, clinicianName);
    setTimeSegment(segment);
  };

  const bookingValid = Boolean(
    values.clinicianId
    && calendar.selectedDate
    && timeslots.selectedSlotStart
    && values.startDateTime
    && values.endDateTime
    && hasBookableSelectedSlot,
  );

  useStepExitTracking({
    stage: deriveBookingStage({
      clinicianChosen: !!values.clinicianId,
      slotsLoading: isLoadingAvailabilities,
      dateChosen: !!calendar.selectedDate,
      bookable: bookingValid,
      submitting,
      failure: localFailure ?? failure ?? null,
    }),
    failure: localFailure ?? failure ?? null,
    feeStatus: deriveFeeStatus({ hasEstimate: !!feeEstimate, loading: feeLoading, unavailable: feeUnavailable }),
    month: calendar.currentMonthString,
    clinicianName,
    nextAvailable,
  }, bookingConfirmedRef);

  useEffect(() => {
    if (feeUnavailable) trackFeeUnavailable();
  }, [feeUnavailable]);

  const handleRetryFee = () => {
    trackFeeRetry();
    onRetryFee();
  };

  const handleBack = () => {
    trackBackClick();
    onBack();
  };

  const selectedDateLabel = calendar.selectedDate
    ? moment(calendar.selectedDate).format('ddd D MMMM YYYY')
    : null;

  const selectedTimeLabel = timeslots.selectedSlotStart
    ? formatTime(timeslots.selectedSlotStart)
    : null;

  const handleFinalSubmit = handleSubmit((data) => {
    if (!data.startDateTime) return;

    const confirm = { clinicianName, startDateTime: data.startDateTime, timing: bookingTimingRef.current };
    trackConfirmClick(confirm);
    if (!moment(data.startDateTime).isAfter(moment())) {
      trackConfirmError(confirm, { kind: 'slot-expired' });
      timeslots.resetSelection();
      setValue('startDateTime', '');
      setValue('endDateTime', '');
      setLocalFailure({ kind: 'slot-expired' });
      return;
    }

    setLocalFailure(null);
    const submission = { ...data };
    if (selectedSpecialist) {
      submission.clinicianName = specialistName(selectedSpecialist);
      submission.clinicianTitle = selectedSpecialist.title || '';
      submission.timezone = selectedSpecialist.timezone;
    }
    onSubmit(submission);
  });

  const feeFooterRow = (
    <FeeFooterRow
      feeEstimate={feeEstimate}
      feeLoading={feeLoading}
      hasMedicareCard={hasMedicareCard}
      onOpenDetails={() => {
        trackFeeDetailsOpen();
        setSheetOpen(true);
      }}
    />
  );

  const getBookingHint = (): string => {
    if (bookingValid) {
      const clinician = clinicianName || 'Selected clinician';
      return `${clinician} · ${selectedDateLabel} at ${selectedTimeLabel}`;
    }
    if (calendar.selectedDate) {
      return 'Choose a time to continue';
    }
    return 'Choose a date and time to continue';
  };

  const bookingHint = getBookingHint();

  const renderClinicianOptions = (mobileStack: boolean) => (
    <ClinicianOptions
      specialists={specialists}
      selectedId={values.clinicianId}
      getAvailability={getAvailability}
      mobileStack={mobileStack}
      onSelect={handleSelectClinician}
    />
  );

  const renderTimeGrid = (compact: boolean) => (
    <TimeGrid
      compact={compact}
      slots={timeslots.availableTimeslots}
      slotsBySegment={slotsBySegment}
      activeSegments={activeSegments}
      timeSegment={timeSegment}
      onSelectSegment={handleSelectSegment}
      selectedDate={calendar.selectedDate}
      selectedSlotStart={timeslots.selectedSlotStart}
      onSelectSlot={handleSelectSlot}
    />
  );

  const clinicianTitle = selectedSpecialist?.title || '';

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-none md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-8">
      <BookingIntro />

      {/* Mobile accordion sections */}
      <div className="md:hidden">
        {isLoadingSpecialists ? (
          <div className="flex justify-center py-8">
            <Spin tip="Loading clinicians..." />
          </div>
        ) : (
          <>
            <MobileBookingSection
              step={1}
              label="Clinician"
              value={clinicianName}
              placeholder="Select a clinician"
              subValue={clinicianTitle}
              done={!!values.clinicianId}
              open={openSection === 1}
              onOpen={() => setOpenSection(1)}
            >
              {renderClinicianOptions(true)}
            </MobileBookingSection>

            {values.clinicianId && (
              <MobileBookingSection
                step={2}
                label="Date"
                value={selectedDateLabel}
                placeholder="Select a date"
                done={!!calendar.selectedDate}
                open={openSection === 2}
                onOpen={() => setOpenSection(2)}
              >
                <MobileDateStrip
                  calendarMonth={calendar.calendarMonth}
                  calendarYear={calendar.calendarYear}
                  dates={monthAvailableDates}
                  selectedDate={calendar.selectedDate}
                  loading={isLoadingAvailabilities}
                  onChangeMonth={handleChangeMonth}
                  onSelectDate={handleSelectDateFromStrip}
                />
              </MobileBookingSection>
            )}

            {calendar.selectedDate && timeslots.availableTimeslots.length > 0 && (
              <MobileBookingSection
                step={3}
                label="Time"
                value={selectedTimeLabel ? `${selectedTimeLabel} AEST` : null}
                placeholder="Select a time"
                done={!!selectedTimeLabel}
                open={openSection === 3}
                onOpen={() => setOpenSection(3)}
              >
                {renderTimeGrid(true)}
              </MobileBookingSection>
            )}
          </>
        )}
      </div>

      {/* Desktop layout (unchanged structure) */}
      <div className="hidden md:block">
        <div className="mb-6">
          <DesktopStepHeading step={1} title="Select a Clinician" />
          {isLoadingSpecialists ? (
            <div className="flex justify-center py-8">
              <Spin tip="Loading clinicians..." />
            </div>
          ) : renderClinicianOptions(false)}
        </div>

        {values.clinicianId && (
          <div className="mb-5">
            <DesktopStepHeading step={2} title="Select an Available Date" />
            <DesktopCalendar
              calendar={calendar}
              availableDates={availableDates}
              loading={isLoadingAvailabilities}
              onChangeMonth={handleChangeMonth}
              onSelectDay={handleSelectDay}
            />
          </div>
        )}

        {calendar.selectedDate && timeslots.availableTimeslots.length > 0 && (
          <div className="mb-5">
            <DesktopStepHeading step={3} title="Select a Time" />
            {renderTimeGrid(false)}
          </div>
        )}
      </div>

      {!feeEstimate && feeLoading && (
        <div className="hidden md:flex bg-slate-50 border border-slate-200 rounded-xl p-4 items-center justify-between">
          <p className="m-0 text-sm font-bold text-slate-700">Fee Summary</p>
          <FeeAmountLoading size="lg" />
        </div>
      )}

      {feeEstimate && (
        <div className="hidden md:block">
          <FeeSummaryDetails
            feeEstimate={feeEstimate}
            feePrescriptionType={feePrescriptionType}
            feeLocation={feeLocation}
            feeAssessmentType={feeAssessmentType}
            feeSeenPsychiatrist={feeSeenPsychiatrist}
            hasMedicareCard={hasMedicareCard}
          />
        </div>
      )}

      <div className="rounded-xl p-3 flex gap-2.5 border mb-2 mt-4 bg-brand bg-opacity-5 border-brand border-opacity-10">
        <p className="text-xs leading-relaxed text-brand-mid">
          You will receive a secure telehealth link via SMS and email prior
          to your telehealth appointment, alongside a secured payment link
          to complete your payment.
        </p>
      </div>

      {feeUnavailable && <FeeUnavailableAlert onRetry={handleRetryFee} />}

      {localFailure ? (
        <SubmitErrorAlert failure={localFailure} />
      ) : (
        failure && <SubmitErrorAlert failure={failure} />
      )}

      <NavRow
        onBack={handleBack}
        onNext={handleFinalSubmit}
        nextDisabled={!bookingValid || submitting || !feeEstimate}
        nextLabel="Confirm Booking"
        loading={submitting}
        footerSlot={feeFooterRow}
        hint={bookingHint}
      />

      {feeEstimate && (
        <BottomSheet
          visible={sheetOpen}
          title="Fee summary"
          onClose={() => setSheetOpen(false)}
        >
          <FeeSummaryDetails
            feeEstimate={feeEstimate}
            feePrescriptionType={feePrescriptionType}
            feeLocation={feeLocation}
            feeAssessmentType={feeAssessmentType}
            feeSeenPsychiatrist={feeSeenPsychiatrist}
            hasMedicareCard={hasMedicareCard}
            compact
          />
        </BottomSheet>
      )}
    </div>
  );
};

export default BookingPage;
